// src/utils/deadlineRules.js
// Centralized appeal-deadline calculation.
// One source of truth: given a parcel + notice date, returns the deadline.
//
// Used by:
//   - DocumentScanner (computes deadline at intake using AI-extracted jurisdiction)
//   - Calendar (computes deadlines for all parcels in portfolio)
//
// IMPORTANT: This file uses ONLY the sync calc function. Callers must call
// loadDeadlineRules() once before calling calcAppealDeadline(). DocumentScanner
// is wrapped to handle this transparently; Calendar awaits the load explicitly.

import { supabase } from '../supabase';

// In-memory cache. Loaded once per session.
let _rulesCache = null;
let _loadPromise = null;

/**
 * Load all active jurisdiction_deadlines rows into memory.
 * Returns a Promise<{count, all, loadedAt}>. Safe to call repeatedly.
 */
export async function loadDeadlineRules() {
  if (_rulesCache) return _rulesCache;
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    const { data, error } = await supabase
      .from('jurisdiction_deadlines')
      .select('*')
      .eq('is_active', true);

    if (error) {
      console.error('[deadlineRules] load error:', error);
      _loadPromise = null;
      throw error;
    }

    _rulesCache = {
      count: data?.length || 0,
      all: data || [],
      loadedAt: new Date(),
    };
    return _rulesCache;
  })();

  return _loadPromise;
}

/**
 * Reset cache. Call after admin edits a rule.
 */
export function resetDeadlineRulesCache() {
  _rulesCache = null;
  _loadPromise = null;
}

// ── Internal: pick the most-specific rule for a state/jurisdiction ──
function pickRule(rules, ST, jurisdiction) {
  const findRow = (name) => rules.find(r => r.jurisdiction === name);
  let rule = null;

  if (ST === 'CA') {
    const CA_NOTICE = new Set(['Alameda','Alpine','Inyo','Kings','Mono','Placer',
      'San Francisco','San Luis Obispo','Santa Clara','Sierra','Ventura']);
    const countyName = (jurisdiction || '').replace(/\s+County$/i, '').trim();
    rule = CA_NOTICE.has(countyName)
      ? findRow('11 Notice Counties')
      : findRow('Other 47 Counties');
  } else if (ST === 'NJ') {
    const adp = ['Monmouth County','Burlington County','Gloucester County'];
    if (jurisdiction && adp.includes(jurisdiction)) rule = findRow(jurisdiction);
    if (!rule) rule = findRow('Most Counties');
  } else if (ST === 'NY' && jurisdiction) {
    const j = jurisdiction.toLowerCase();
    if (j.includes('nassau')) rule = findRow('Nassau County');
    else if (j.includes('class 1')) rule = findRow('NYC (Class 1)');
    else if (j.includes('class 2') || j.includes('class 3') || j.includes('class 4') ||
             j.includes('nyc') || j.includes('new york city')) rule = findRow('NYC (Class 2-4)');
  } else if (ST === 'PA' && jurisdiction) {
    const j = jurisdiction.toLowerCase();
    if (j.includes('allegheny')) rule = findRow('Allegheny County');
    else if (j.includes('philadelphia')) rule = findRow('Philadelphia');
  }
  if (!rule && jurisdiction) rule = findRow(jurisdiction);
  if (!rule) rule = findRow('Statewide');
  // For NJ, "Statewide" doesn't exist — use "Most Counties" as fallback
  if (!rule && ST === 'NJ') rule = findRow('Most Counties');
  // For CA, "Statewide" doesn't exist — use "Other 47 Counties"
  if (!rule && ST === 'CA') rule = findRow('Other 47 Counties');
  // For PA, "Statewide" doesn't exist — use "Most Counties..."
  if (!rule && ST === 'PA') rule = findRow('Most Counties (excl Allegheny/Philadelphia)');
  // MO has one rule ('Most Counties'), no 'Statewide' row, and no branch above — it relied on the
  // removed rules[0] fallback. Restore its default explicitly so unmatched MO returns the right rule.
  if (!rule && ST === 'MO') rule = findRow('Most Counties');
  if (!rule) return null;   // NO first-row fallback: an unmatched jurisdiction returns NO deadline,
                            // never rules[0]'s wrong statutory date. Inert while rules is empty (returns
                            // null either way); load-bearing once jurisdiction_deadlines is readable (Batch 9).
  return rule || null;
}

// ── Internal: build a Date in the SAME calendar year as notice date ──
// Critical: we do NOT roll forward to next year if the date has passed.
// Past deadlines should remain past so they correctly display as "Missed"
// in the calendar UI. The original Phase 4 logic rolled forward, which is
// wrong for both late notices and historical data.
function buildFixedDate(year, month, day) {
  return new Date(year, month - 1, day);
}

// ── Internal: resolve a JSONB formula to a Date ──
function resolveFormula(f, nd) {
  if (!f || !f.type) return null;
  switch (f.type) {
    case 'fixed': {
      if (!f.month || !f.day) return null;
      return buildFixedDate(nd.getFullYear(), f.month, f.day);
    }
    case 'notice_plus_days': {
      if (!f.days) return null;
      const d = new Date(nd);
      d.setDate(d.getDate() + f.days);
      return d;
    }
    case 'later_of': {
      const cands = (f.options || [])
        .map(o => resolveFormula(o, nd))
        .filter(d => d && !isNaN(d));
      if (!cands.length) return null;
      return new Date(Math.max(...cands.map(d => d.getTime())));
    }
    case 'earlier_of': {
      const cands = (f.options || [])
        .map(o => resolveFormula(o, nd))
        .filter(d => d && !isNaN(d));
      if (!cands.length) return null;
      return new Date(Math.min(...cands.map(d => d.getTime())));
    }
    case 'varies_by_county':
    case 'varies_by_locality':
      return f.fallback ? resolveFormula(f.fallback, nd) : null;
    case 'fallback_no_notice':
      if (f.month && f.day) {
        return buildFixedDate(nd.getFullYear(), f.month, f.day);
      }
      return null;
    default:
      console.warn('[deadlineRules] unknown formula type:', f.type);
      return null;
  }
}

// ── Internal: format a JS Date as 'YYYY-MM-DD' using LOCAL time ──
// We avoid toISOString() because it converts to UTC, which shifts dates
// backwards for users in negative-offset timezones (TX users would see
// May 14 instead of May 15 for late-night dates).
function formatIsoDate(d) {
  if (!d || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Internal: apply a chosen rule to a notice date ──
function applyRule(rule, nd) {
  if (rule.complex_rule_formula) {
    const d = resolveFormula(rule.complex_rule_formula, nd);
    if (d && !isNaN(d)) return formatIsoDate(d);
  }
  if (rule.rule_type === 'notice_plus_days' && rule.notice_plus_days) {
    const d = new Date(nd);
    d.setDate(d.getDate() + rule.notice_plus_days);
    return formatIsoDate(d);
  }
  if (rule.rule_type === 'fixed' && rule.fixed_month && rule.fixed_day) {
    return formatIsoDate(buildFixedDate(nd.getFullYear(), rule.fixed_month, rule.fixed_day));
  }
  if (rule.rule_type === 'complex' && rule.notice_plus_days) {
    // Safety fallback for complex rules without formula
    const d = new Date(nd);
    d.setDate(d.getDate() + rule.notice_plus_days);
    return formatIsoDate(d);
  }
  return null;
}

// ═══ describeRule — THE SAME LADDER AS applyRule, IN ENGLISH ═══
//
// ⛔ IT SITS HERE, TOUCHING applyRule, ON PURPOSE. The Jurisdiction Deadlines report has to say
// what each rule MEANS, and a second interpreter written in the report layer would drift from this
// one the first time a branch changed — the report would then describe a deadline the Calendar
// does not compute. Keeping both ladders adjacent makes the coupling visible: CHANGE applyRule AND
// THIS TOGETHER, always.
//
// ⛔ IT MIRRORS THE PRECEDENCE, WHICH IS NOT OBVIOUS: complex_rule_formula WINS over the typed
// columns. AR carries rule_type 'fixed' with fixed_month/fixed_day AND a formula; the formula is
// what runs. So describing a rule from rule_type alone would be wrong for 13 of the 63 rules.
//
// ⛔ describeFormula RETURNS NULL EXACTLY WHERE resolveFormula RETURNS NULL, so the fall-through to
// the typed columns happens at the same points in both. A varies_by_county rule with no fallback
// resolves to nothing, so it describes as nothing and drops to the next rung — matching applyRule
// rather than reading as though it produced an answer.
const RULE_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function describeFormula(f) {
  if (!f || !f.type) return null;
  switch (f.type) {
    case 'fixed':
      return (f.month && f.day) ? `${RULE_MONTHS[f.month - 1]} ${f.day}` : null;
    case 'notice_plus_days':
      return f.days ? `notice + ${f.days} days` : null;
    case 'later_of':
    case 'earlier_of': {
      const parts = (f.options || []).map(describeFormula).filter(Boolean);
      if (!parts.length) return null;
      return `${f.type === 'later_of' ? 'later' : 'earlier'} of ${parts.join(' or ')}`;
    }
    case 'varies_by_county':
    case 'varies_by_locality': {
      // resolveFormula returns null without a fallback, so this must too.
      const fb = f.fallback ? describeFormula(f.fallback) : null;
      if (!fb) return null;
      return `varies by ${f.type === 'varies_by_county' ? 'county' : 'locality'} — ${fb} assumed`;
    }
    case 'fallback_no_notice':
      return (f.month && f.day) ? `${RULE_MONTHS[f.month - 1]} ${f.day} if no notice` : null;
    default:
      return null;
  }
}

/**
 * Plain-English statement of what a rule computes, or NULL when it computes nothing.
 *
 * ⛔ A NULL RETURN IS A FINDING, NOT A FORMATTING GAP. It means applyRule would also produce no
 * date: the rule row exists, Calendar matches it, and no deadline comes out. On the current data
 * that is every rule_type 'complex' row carrying neither a formula nor notice_plus_days.
 */
export function describeRule(rule) {
  if (!rule) return null;
  const fromFormula = rule.complex_rule_formula ? describeFormula(rule.complex_rule_formula) : null;
  if (fromFormula) return fromFormula;
  if (rule.rule_type === 'notice_plus_days' && rule.notice_plus_days) return `notice + ${rule.notice_plus_days} days`;
  if (rule.rule_type === 'fixed' && rule.fixed_month && rule.fixed_day) return `${RULE_MONTHS[rule.fixed_month - 1]} ${rule.fixed_day}`;
  if (rule.rule_type === 'complex' && rule.notice_plus_days) return `notice + ${rule.notice_plus_days} days`;
  return null;
}

/**
 * Parse a date string or Date safely.
 * Accepts: 'YYYY-MM-DD', JS Date, ISO timestamp.
 * Returns a Date or null. Always interprets bare YYYY-MM-DD as LOCAL midnight,
 * not UTC midnight — important for date-only fields like notice_date.
 */
function parseDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === 'string') {
    // Match 'YYYY-MM-DD' and parse as local midnight to avoid timezone shifts
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const d = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Calculate appeal deadline for a single parcel.
 * Returns ISO date string ('YYYY-MM-DD') or null.
 *
 * REQUIRES that loadDeadlineRules() has been called and resolved before this.
 * The Calendar awaits it explicitly; DocumentScanner uses calcAppealDeadlineAsync
 * which handles the load internally.
 */
export function calcAppealDeadline(state, noticeDate, jurisdiction) {
  if (!_rulesCache) {
    console.warn('[calcAppealDeadline] cache not loaded — call loadDeadlineRules() first');
    return null;
  }
  if (!state || !noticeDate) return null;

  const ST = state.toUpperCase();
  const nd = parseDate(noticeDate);
  if (!nd) return null;

  const rules = _rulesCache.all.filter(r => r.state === ST);
  if (!rules.length) return null;

  const rule = pickRule(rules, ST, jurisdiction);
  if (!rule) return null;
  return applyRule(rule, nd);
}

/**
 * Async wrapper for callers that haven't pre-loaded rules.
 * Used by DocumentScanner to keep its existing call pattern.
 */
export async function calcAppealDeadlineAsync(state, noticeDate, jurisdiction) {
  try {
    await loadDeadlineRules();
    return calcAppealDeadline(state, noticeDate, jurisdiction);
  } catch (e) {
    console.error('[calcAppealDeadlineAsync] error:', e);
    return null;
  }
}

/**
 * Compute urgency tier from a deadline date.
 * Used by calendar UI for color coding.
 *
 * Uses local-time date math (no UTC conversions) to avoid the "11 PM Texas
 * time shows tomorrow's deadline as today" bug.
 */
export function urgencyTier(deadline, today = new Date()) {
  const dd = parseDate(deadline);
  if (!dd) return null;
  // Build both dates as local midnight
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const ddMid = new Date(dd.getFullYear(), dd.getMonth(), dd.getDate());
  const ms = ddMid.getTime() - todayMid.getTime();
  const days = Math.round(ms / (1000 * 60 * 60 * 24));

  if (days < 0) return { tier: 'missed', days, label: `${Math.abs(days)}d past` };
  if (days === 0) return { tier: 'today', days, label: 'Today' };
  if (days <= 7) return { tier: 'on_us', days, label: `${days}d` };
  if (days <= 30) return { tier: 'coming_up', days, label: `${days}d` };
  return { tier: 'far_out', days, label: `${days}d` };
}

/**
 * Color tokens for each urgency tier (light and dark variants).
 */
export const URGENCY_COLORS = {
  far_out:   { bg: '#e8eaf2', fg: 'var(--slate)', border: 'var(--line)' },
  coming_up: { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
  on_us:     { bg: '#fed7aa', fg: '#9a3412', border: '#fdba74' },
  today:     { bg: '#fecaca', fg: '#991b1b', border: '#fca5a5' },
  missed:    { bg: '#7f1d1d', fg: '#fee2e2', border: '#991b1b' },
};

export const URGENCY_COLORS_DARK = {
  far_out:   { bg: 'rgba(107,116,153,0.15)', fg: '#a8aec5', border: 'rgba(107,116,153,0.3)' },
  coming_up: { bg: 'rgba(245,158,11,0.15)', fg: '#fbbf24', border: 'rgba(245,158,11,0.4)' },
  on_us:     { bg: 'rgba(249,115,22,0.15)', fg: '#fb923c', border: 'rgba(249,115,22,0.4)' },
  today:     { bg: 'rgba(239,68,68,0.20)', fg: '#fca5a5', border: 'rgba(239,68,68,0.5)' },
  missed:    { bg: 'rgba(127,29,29,0.40)', fg: '#fecaca', border: '#7f1d1d' },
};
